import React from "react";
import { Box, IconButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Remove } from "@mui/icons-material";
import KrgDataTable from "@kargo/shared-components.krg-data-table";
import dayjs from "dayjs";
import { THUMBNAIL_STYLE } from "../../../../constants/constants";
import { Video } from "../../../../api/types";

export const useStyles = makeStyles({
  container: {
    maxHeight: "400px",
    overflowY: "auto",
  },
});
interface PlaylistDataTableProps {
  videos: Video[];
  onRemove: (videoToken: string) => void;
  rowSize?: number;
  showRemoveButton?: boolean;
}

const PlaylistDataTable: React.FC<PlaylistDataTableProps> = ({
  videos,
  onRemove,
  rowSize = 6,
  showRemoveButton = true,
}) => {
  const classes = useStyles();

  const columns = [
    {
      headerName: "",
      field: "number",
      width: "50px",
      renderCell: (row: any) => {
        const index = videos.findIndex((v) => v.token === row.id);
        return (
          <Box
            sx={{
              textAlign: "center",
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(showRemoveButton && {
                "& .remove-button": {
                  position: "absolute",
                  opacity: 0,
                  transition: "opacity 0.2s",
                },
              }),
            }}
          >
            <span
              className="number-text"
              style={{
                textAlign: "center",
                display: "block",
                transition: "opacity 0.2s",
              }}
            >
              {index + 1}
            </span>
            {showRemoveButton && (
              <IconButton
                className="remove-button"
                size="small"
                onClick={() => onRemove(row.id)}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: "rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                  },
                }}
              >
                <Remove fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      headerName: "Thumbnail",
      field: "thumbnail",
      width: "100px",
      renderCell: (row: any) => (
        <img
          src={row.thumbnail}
          alt={row.title}
          style={THUMBNAIL_STYLE}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ),
    },
    {
      headerName: "Video Title",
      field: "title",
      minWidth: "300px",
      maxWidth: "400px",
    },
    {
      headerName: "Publish Date",
      field: "publish_date",
      width: "120px",
      textAlignment: "right" as const,
    },
  ];

  return (
    <Box
      sx={{
        ...(showRemoveButton && {
          "& .MuiDataGrid-row": {
            "&:hover .remove-button": {
              opacity: "1 !important",
            },
            "&:hover .number-text": {
              opacity: "0 !important",
            },
          },
          "& .MuiTableRow-root": {
            "&:hover .remove-button": {
              opacity: "1 !important",
            },
            "&:hover .number-text": {
              opacity: "0 !important",
            },
          },
        }),
      }}
    >
      <KrgDataTable
        theme={KrgDataTable.THEME_ENUM.v2}
        containerClassName={classes.container}
        config={{
          data: {
            columns,
            rows: videos.map((video) => ({
              id: video.token,
              thumbnail: video?.default_stream?.image_url,
              title: video.title,
              publish_date: dayjs(video.created_at).format("MM/DD/YYYY"),
            })),
          },
          pagination: {
            initialPageSize: rowSize,
            totalRowCount: videos.length,
            hasStrictRows: false,
          },
        }}
      />
    </Box>
  );
};

export default PlaylistDataTable;
